import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Gamepad2, Coins, Star, Calendar, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

export const PlayerFeatures = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: Coins,
      title: "Earn Coins",
      description: "Complete daily missions and earn coins to purchase indie games"
    },
    {
      icon: Gamepad2,
      title: "Discover Games",
      description: "Explore our curated collection of indie games and discover your next favorite"
    },
    {
      icon: Calendar,
      title: "Release Calendar",
      description: "Track upcoming game releases and never miss a launch"
    },
    {
      icon: Trophy,
      title: "Achievements",
      description: "Unlock badges and achievements as you explore and test games"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-4 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="container">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="outline" className="mb-4 px-4 py-2 text-sm">
            <Users className="w-4 h-4 mr-2" />
            For Everyone
          </Badge>
          <h2 className="text-[2.5rem] md:text-[4rem] font-bold mb-6">
            Not Just for <span className="gradient-text">Creators</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            IndieKeys is open to all gamers! Whether you're a content creator, developer, or just a passionate gamer, 
            you can discover amazing indie games, earn rewards, and be part of our community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {features.map((feature, index) => (
            <Card 
              key={feature.title} 
              className={`p-6 text-center hover:shadow-lg transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                <feature.icon className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
              <p className="text-muted-foreground text-sm">{feature.description}</p>
            </Card>
          ))}
        </div>

        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '400ms' }}>
          <Button 
            size="lg" 
            onClick={() => navigate("/auth")}
            className="px-8 py-3 text-lg"
          >
            Join the Community
          </Button>
        </div>
      </div>
    </section>
  );
};
